import { catalogResponseSchema, catalogCategorySchema, catalogFrameworkStateSchema, newCustomCategorySchema, editCustomCategorySchema, editBuiltinCategorySchema, catalogToggleSchema, customCategorySchema, CatalogId, CATALOG_ROUTE_SEGMENT, } from '../contracts/ai-instruction-catalog';
import { organizationIdSchema } from '../contracts/organizations';
import { parseSchema } from '../errors';
/**
 * AI Instruction Catalog API helpers. Each takes an optional `organizationId`
 * (present → org-scoped route, omitted → the current user's personal/account
 * route) and a `catalog` (KIR-3816; defaults to `secure_coding`). The backend
 * serves both scopes and both catalogs from one `registerCatalogRoutes`
 * registrar, so there is a single function per operation.
 */
function catalogBase(organizationId, catalog) {
    const segment = CATALOG_ROUTE_SEGMENT[catalog];
    if (organizationId === undefined) {
        return `/account/${segment}`;
    }
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    return `/organizations/${validatedId}/${segment}`;
}
/**
 * Get a catalog: built-in (OWASP / MITRE ATLAS / NIST SSDF) + out-of-the-box and
 * custom categories, per-framework state, and counts.
 */
export async function getAiInstructionCatalog(client, organizationId, catalog = CatalogId.SecureCoding) {
    const response = await client.get(catalogBase(organizationId, catalog));
    return parseSchema(catalogResponseSchema, response);
}
/**
 * Add a custom category.
 */
export async function addCustomCatalogCategory(client, organizationId, category, catalog = CatalogId.SecureCoding) {
    const validatedData = parseSchema(newCustomCategorySchema, category);
    const response = await client.post(`${catalogBase(organizationId, catalog)}/categories`, validatedData);
    return parseSchema(customCategorySchema, response);
}
/**
 * Edit all fields of a custom category.
 */
export async function editCustomCatalogCategory(client, organizationId, categoryId, category, catalog = CatalogId.SecureCoding) {
    const validatedData = parseSchema(editCustomCategorySchema, category);
    const response = await client.put(`${catalogBase(organizationId, catalog)}/categories/${encodeURIComponent(categoryId)}`, validatedData);
    return parseSchema(customCategorySchema, response);
}
/**
 * Enable/disable a single category (built-in slug or custom uuid).
 */
export async function setCatalogCategoryEnabled(client, organizationId, categoryId, enabled, catalog = CatalogId.SecureCoding) {
    const body = parseSchema(catalogToggleSchema, { enabled });
    const response = await client.put(`${catalogBase(organizationId, catalog)}/categories/${encodeURIComponent(categoryId)}/enabled`, body);
    return parseSchema(catalogCategorySchema, response);
}
/**
 * Edit the editable fields (severity, keywords, example) of a built-in category.
 * Name and reference are locked server-side.
 */
export async function editBuiltinCatalogCategory(client, organizationId, builtinSlug, edit, catalog = CatalogId.SecureCoding) {
    const validatedData = parseSchema(editBuiltinCategorySchema, edit);
    const response = await client.put(`${catalogBase(organizationId, catalog)}/builtin/${encodeURIComponent(builtinSlug)}`, validatedData);
    return parseSchema(catalogCategorySchema, response);
}
/**
 * Enable/disable a whole framework.
 */
export async function setCatalogFrameworkEnabled(client, organizationId, framework, enabled, catalog = CatalogId.SecureCoding) {
    const body = parseSchema(catalogToggleSchema, { enabled });
    const response = await client.put(`${catalogBase(organizationId, catalog)}/frameworks/${encodeURIComponent(framework)}/enabled`, body);
    return parseSchema(catalogFrameworkStateSchema, response);
}
/**
 * Remove a custom category. Built-in categories cannot be removed (404/403).
 */
export async function removeCustomCatalogCategory(client, organizationId, categoryId, catalog = CatalogId.SecureCoding) {
    await client.delete(`${catalogBase(organizationId, catalog)}/categories/${encodeURIComponent(categoryId)}`);
}
//# sourceMappingURL=ai-instruction-catalog.js.map